import { useEffect, useMemo, useState } from "react";
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types } from "../../../../../Definitions";
import { colors, fonts, frames, glyphs } from "../../../../../Styles";
import NewButton from "../../../../../Components/BaseComponents/newButton";
import { AicoConnection, PageHeader, useAicoConnection } from "../../../../../Components";
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel } from "@tanstack/react-table";
import { useColumnConstructor } from "../../../../../Components/Tables/_Tables/useColumnConstructor";
import { ReactiveTable } from "../../../../../Components/Tables/reactiveTableRenderer";
import { useLazyQuery } from "@apollo/client";
import { DEVICE_CHANGE_QUEUE_INFO, DeviceChangeQueueInfoArgs, DeviceChangeQueueInfoResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/deviceChangeQueueInfo";
import { LoadingSpinner } from "../../../../../Services/wrapper";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "CPU ID", accessor: "lan_controller_cpu_id", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "Messages", accessor: "message_count", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "Oldest message", accessor: "oldest_message_timestamp", type: ListData_ValueTypes.Date, sort: true },
	{ headerValue: "error", accessor: "error_occurred", type: ListData_ValueTypes.Div, sort: true },
]

export default function DeviceChangeQueue() {
	const limit = 50;

	const [pagination, setPagination] = useState({
		pageIndex: 0, //initial page index
		pageSize: limit,
	});

	const { state: connectionState } = useAicoConnection();

	const [fetchData, { data: data, loading: loading, error: error, }] = useLazyQuery<DeviceChangeQueueInfoResponse, DeviceChangeQueueInfoArgs>(DEVICE_CHANGE_QUEUE_INFO, {
		fetchPolicy: "network-only",
	});

	useEffect(() => {
		if (connectionState.domain) {
			setPagination({ pageIndex: 0, pageSize: limit });
			fetchData({
				variables: {
					domain: connectionState.domain,
					input: {
						fetchAmount: limit + 1,
						offset: 0
					}
				}
			})
		} else {
			setPagination({ pageIndex: 0, pageSize: limit });
			fetchData(); //TODO: find a better way to do this, as this will always give an err?
		}
	}, [connectionState.domain]);

	const tableData = useMemo(() => {
		return data?.controller.deviceChangeQueueInfo.map(e => {
			return {
				...e,
				id: e.lan_controller_id,
				oldest_message_timestamp: e.oldest_message_timestamp && !isNaN(parseInt(e.oldest_message_timestamp)) ? parseInt(e.oldest_message_timestamp) * 1000 : undefined,
				error_occurred: e.error_occurred ? <glyphs.Error fillColor={colors.systemRed} /> : undefined
			}
		}) ?? [];
	}, [data]);

	const columns = useColumnConstructor({
		accessorData: columnsData,
		tableStyle: TableStyles_Types.primaryTable,
		canEdit: false,
	});

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onPaginationChange: setPagination,
		state: {
			pagination,
		},
		manualPagination: true,
  		pageCount: tableData.length > limit ? pagination.pageIndex + 2 : pagination.pageIndex + 1,
	});

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Device Data Tables' subHeader='Queue' />
			<frames._page_inner_content>
				<frames._grid_topBtns_outer>
					<frames._grid_topBtns_inner style={{ position: "relative", justifyContent: "end" }}>
						<div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: "20px" }}><AicoConnection hideLANControllers /></div>
						<NewButton
							content={"fetch"}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.domain) {
									fetchData({
										variables: {
											domain: connectionState.domain,
											input: {
												fetchAmount: limit + 1,
												offset: pagination.pageIndex * limit,
											},
										},
									});
								}
							}}
							disabled={loading || !connectionState.domain}
						/>
					</frames._grid_topBtns_inner>
					<div style={{ gridArea: "2/1/3/2", border: "5px solid " + colors.greyPrimary, overflow: "hidden", borderRadius: "10px", backgroundColor: colors.greyLightTertiary }}>
						{loading && <LoadingSpinner />}
						{error && <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px" }}>ERROR: failed to fetch data</p>}
						{!loading && !error &&
							<ReactiveTable
								table={table}
								tableType={TableStyles_Types.primaryTable}
								emptyText="No data found"
								renderFooter={false}
							/>
						}
					</div>
				</frames._grid_topBtns_outer>
			</frames._page_inner_content>
			<div style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", gap: "5px", padding: "5px 20px 0px 20px" }}>
				<NewButton
					content={""}
					buttonType={ButtonTypes.arrowButton}
					onClick={() => {
						if (table.getCanPreviousPage() && connectionState.domain) {
							fetchData({ variables: { domain: connectionState.domain, input: { fetchAmount: limit + 1, offset: limit * (table.getState().pagination.pageIndex - 1) } } })
							table.previousPage()
						}
					}}
					startEnhancer={glyphs.Arrow({ rotate: 180 })}
					overrides={{
						baseStyles: { backgroundColor: colors.greyPrimary },
						hoverStyles: { backgroundColor: colors.greySecondary },
					}}
					disabled={!table.getCanPreviousPage()}
				/>
				<NewButton
					content={""}
					buttonType={ButtonTypes.arrowButton}
					onClick={() => {
						if (table.getCanNextPage() && connectionState.domain) {
							fetchData({ variables: { domain: connectionState.domain, input: { fetchAmount: limit + 1, offset: limit * (table.getState().pagination.pageIndex + 1) } } })
							table.nextPage()
						}
					}}
					startEnhancer={glyphs.Arrow({})}
					overrides={{
						baseStyles: { backgroundColor: colors.greyPrimary },
						hoverStyles: { backgroundColor: colors.greySecondary },
					}}
					disabled={!table.getCanNextPage()}
				/>
				<p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
					{`page ${table.getState().pagination.pageIndex + 1} of ${table.getPageCount()}`}
				</p>
			</div>
		</frames.Page_inner>
	)
}